import { useEffect, useState } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, inr, formatErr } from "@/lib/api";
import { GST_RATES } from "@/lib/quote";

const EMPTY = { name: "", description: "", hsn: "", unit: "Nos", rate: "", gst_rate: 18, active: true };

const ProductForm = ({ initial, onSaved }) => {
  const [f, setF] = useState(initial ? { ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, description: f.description, hsn: f.hsn, unit: f.unit, rate: Number(f.rate || 0), gst_rate: Number(f.gst_rate), active: f.active };
      if (initial?.id) await api.put(`/products/${initial.id}`, body);
      else await api.post("/products", body);
      toast.success("Product saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="product-form">
      <div><Label>Product Name</Label><Input required value={f.name} onChange={set("name")} placeholder="e.g. 5 Function Electric ICU Bed" data-testid="product-name-input" /></div>
      <div><Label>Description / Specs</Label><Textarea value={f.description} onChange={set("description")} data-testid="product-desc-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>HSN / SAC</Label><Input value={f.hsn} onChange={set("hsn")} data-testid="product-hsn-input" /></div>
        <div><Label>Unit</Label><Input value={f.unit} onChange={set("unit")} data-testid="product-unit-input" /></div>
        <div><Label>Rate (₹, before GST)</Label><Input required type="number" min="0" step="any" value={f.rate} onChange={set("rate")} data-testid="product-rate-input" /></div>
        <div><Label>GST %</Label>
          <Select value={String(f.gst_rate)} onValueChange={(v) => setF((p) => ({ ...p, gst_rate: Number(v) }))}>
            <SelectTrigger data-testid="product-gst-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{GST_RATES.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <label className="flex items-center justify-between border rounded-lg p-3 text-sm">Show in catalogue<Switch checked={f.active} onCheckedChange={set("active")} data-testid="product-active-switch" /></label>
      <Button type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="product-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Product"}</Button>
    </form>
  );
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/products", { params: { all: true } }).then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Product Catalogue</h1>
          <p className="text-sm text-slate-500 mt-1">Standard prices used in quotations. Staff can still adjust price per quote.</p>
        </div>
        <Button onClick={() => setEdit(null)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-product-button"><Plus className="h-4 w-4 mr-2" />Add Product</Button>
      </div>
      <div className="bg-white border rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Product</TableHead><TableHead>HSN</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">GST</TableHead><TableHead>Status</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No products yet. Add your ICU beds, monitors, MGPS items, etc.</TableCell></TableRow>}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`} className={p.active ? "" : "opacity-50"}>
                <TableCell><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-500 line-clamp-1">{p.description}</div></TableCell>
                <TableCell className="text-sm">{p.hsn || "—"}</TableCell>
                <TableCell className="text-sm">{p.unit}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(p.rate)}</TableCell>
                <TableCell className="text-right font-mono-num">{p.gst_rate}%</TableCell>
                <TableCell className="text-xs">{p.active ? "Active" : "Hidden"}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => setEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        <DialogContent className="bg-white max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{edit ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          {edit !== undefined && <ProductForm initial={edit} onSaved={() => { setEdit(undefined); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
